import { useCallback, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

/**
 * Parameters for updating an existing entity record.
 */
export interface UseUpdateEntityParams {
  entity: string;
  id: string | number;
}

/**
 * Result returned by the update hook, including submission state.
 */
export interface UseUpdateEntityResult<TInput, TOutput> {
  update: (values: TInput) => Promise<TOutput | undefined>;
  isSubmitting: boolean;
  isError: boolean;
  error: unknown;
  data: TOutput | undefined;
}

/**
 * Issues a PUT request to update an entity using the Pericles client configuration.
 */
export function useUpdateEntity<TInput = unknown, TOutput = TInput>(
  params: UseUpdateEntityParams
): UseUpdateEntityResult<TInput, TOutput> {
  const { baseUrl, fetcher } = usePericlesClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(undefined);
  const [data, setData] = useState<TOutput | undefined>(undefined);

  const base = useMemo(() => baseUrl.replace(/\/$/, ''), [baseUrl]);

  const url = useMemo(
    () =>
      `${base}/entities/${buildEntityPathSegment(params.entity)}/${encodeURIComponent(String(params.id))}`,
    [base, params.entity, params.id]
  );

  const update = useCallback(
    async (values: TInput) => {
      setIsSubmitting(true);
      setIsError(false);
      setError(undefined);

      try {
        const response = await fetcher(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        let json: TOutput | undefined;

        try {
          json = (await response.json()) as TOutput;
        } catch {
          json = undefined;
        }

        setData(json);
        return json;
      } catch (err) {
        setIsError(true);
        setError(err);
        setData(undefined);
        return undefined;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetcher, url]
  );

  return {
    update,
    isSubmitting,
    isError,
    error,
    data,
  };
}
